import React from 'react';
import {
  Alert,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {
  useBillingReconciliationSuggestedMatches,
  useConfirmBillingReconciliationSuggestedMatch,
  useRejectBillingReconciliationSuggestedMatch,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const RecurringSuggestionsPanel = ({ runId, varianceId, bac, query: providedQuery = null }) => {
  const localQuery = useBillingReconciliationSuggestedMatches(
    { runId, bac },
    { enabled: Boolean(runId && bac && !providedQuery) },
  );
  const query = providedQuery || localQuery;
  const confirm = useConfirmBillingReconciliationSuggestedMatch();
  const reject = useRejectBillingReconciliationSuggestedMatch();
  const suggestions = query.data?.rows || [];

  if (!runId || !bac || query.isLoading || query.isError || suggestions.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, borderColor: 'info.light' }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeOutlinedIcon color="info" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 900 }}>
            Suggested recurring matches
          </Typography>
          <Chip size="small" color="info" variant="outlined" label={`${suggestions.length} suggestion${suggestions.length === 1 ? '' : 's'}`} />
        </Stack>
        <Alert severity="info" icon={false}>
          Suggestions are review-only until confirmed. They will not overwrite manual evidence.
        </Alert>
        {suggestions.map((suggestion) => (
          <Stack
            key={suggestion.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {suggestion.productCode || suggestion.productFamily} - {suggestion.salesforceAccountName || suggestion.salesforceAccountId || 'No account'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {suggestion.sourceSystem} {suggestion.orderItemId || suggestion.referenceId || 'evidence'} - {suggestion.status}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={suggestion.status === 'confirmed' ? 'outlined' : 'contained'}
                disabled={suggestion.status === 'confirmed' || !varianceId || confirm.isPending}
                onClick={() => confirm.mutate({
                  runId,
                  bac,
                  ruleId: suggestion.ruleId,
                  varianceId: suggestion.varianceId || varianceId,
                  note: 'Confirmed from recurring suggestion panel.',
                })}
              >
                {suggestion.status === 'confirmed' ? 'Applied' : 'Confirm'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={reject.isPending}
                onClick={() => reject.mutate({
                  runId,
                  bac,
                  ruleId: suggestion.ruleId,
                  varianceId: suggestion.varianceId || varianceId,
                  status: 'rejected',
                  note: 'Rejected from recurring suggestion panel.',
                })}
              >
                Reject
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export default RecurringSuggestionsPanel;
